/**
 * 관리자 대리인(Agent) 관리 (M13) React Query 훅.
 *
 * - 조회:
 *     ``useAgents({ includeInactive })`` — 60초 staleTime.
 *     ``useAgent(id)``                    — 편집 모달 / 상세 페이지 prefetch 용.
 * - 변이 3종: 생성 / 수정 / 비활성화(soft delete).
 *   변이 성공 시 list 캐시는 일괄 무효화, single 캐시는 setQueryData 로 즉시 반영.
 *
 * 캐시 키 규약:
 *   ["admin", "agents", "list", { includeInactive }]
 *   ["admin", "agents", "detail", id]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  applyAgentRulesImport,
  createAgent,
  deleteAgent,
  fetchAgentStats,
  getAgent,
  listAgents,
  matchTestAgents,
  previewAgentRulesImport,
  updateAgent,
  type AgentCreatePayload,
  type AgentMatchTestResponse,
  type AgentOut,
  type AgentRulesFile,
  type AgentRulesImportPreview,
  type AgentStatsResponse,
  type AgentUpdatePayload,
  type FetchAgentStatsParams,
} from "@/lib/api/agents";

// ────────────── 캐시 키 ──────────────

export const AGENTS_QUERY_KEY = ["admin", "agents"] as const;

export const agentsKeys = {
  all: AGENTS_QUERY_KEY,
  lists: () => [...AGENTS_QUERY_KEY, "list"] as const,
  list: (includeInactive: boolean) => [...AGENTS_QUERY_KEY, "list", { includeInactive }] as const,
  details: () => [...AGENTS_QUERY_KEY, "detail"] as const,
  detail: (id: string) => [...AGENTS_QUERY_KEY, "detail", id] as const,
  stats: (params: FetchAgentStatsParams = {}) =>
    [...AGENTS_QUERY_KEY, "stats", { from: params.from ?? null, to: params.to ?? null }] as const,
};

// ────────────── 조회 ──────────────

export interface UseAgentsOptions {
  includeInactive?: boolean;
  enabled?: boolean;
}

export function useAgents(options: UseAgentsOptions = {}) {
  const { includeInactive = true, enabled = true } = options;
  return useQuery<AgentOut[]>({
    queryKey: agentsKeys.list(includeInactive),
    queryFn: () => listAgents({ includeInactive }),
    staleTime: 60_000,
    enabled,
  });
}

export function useAgent(id: string | null | undefined) {
  return useQuery<AgentOut>({
    queryKey: agentsKeys.detail(id ?? ""),
    queryFn: () => {
      if (!id) {
        // enabled 가드로 호출되지 않지만, 타입 안전성을 위해 명시.
        throw new Error("agent id is required");
      }
      return getAgent(id);
    },
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

// ────────────── 변이 ──────────────

function invalidateAgentLists(qc: ReturnType<typeof useQueryClient>) {
  // list("true") / list("false") 양쪽을 한 번에 무효화.
  void qc.invalidateQueries({ queryKey: agentsKeys.lists() });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation<AgentOut, Error, AgentCreatePayload>({
    mutationFn: createAgent,
    onSuccess: (created) => {
      qc.setQueryData(agentsKeys.detail(created.id), created);
      invalidateAgentLists(qc);
    },
  });
}

interface UpdateAgentVars {
  agentId: string;
  payload: AgentUpdatePayload;
}

export function useUpdateAgent() {
  const qc = useQueryClient();
  return useMutation<AgentOut, Error, UpdateAgentVars>({
    mutationFn: ({ agentId, payload }) => updateAgent(agentId, payload),
    onSuccess: (updated) => {
      qc.setQueryData(agentsKeys.detail(updated.id), updated);
      invalidateAgentLists(qc);
    },
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => deleteAgent(agentId),
    onSuccess: (_data, agentId) => {
      // soft delete → 행은 살아있고 is_active=false. detail 캐시는 invalidate 로 재조회.
      void qc.invalidateQueries({ queryKey: agentsKeys.detail(agentId) });
      invalidateAgentLists(qc);
    },
  });
}

/**
 * 임의 텍스트로 매칭 결과를 받아오는 mutation (C-2).
 *
 * `useMutation` 을 사용하는 이유: 캐싱하지 않아야 하며 (텍스트가 매번 달라짐),
 * 사용자 액션(버튼 클릭) 으로 명시적으로 트리거되어야 함.
 */
export function useMatchTest() {
  return useMutation<AgentMatchTestResponse, Error, string>({
    mutationFn: matchTestAgents,
  });
}

/**
 * Agent 매칭 통계 조회 (C-3).
 *
 * 통계 모달이 열릴 때만 호출되도록 ``enabled`` 옵션을 노출.
 */
export interface UseAgentStatsOptions extends FetchAgentStatsParams {
  enabled?: boolean;
}

export function useAgentStats(options: UseAgentStatsOptions = {}) {
  const { enabled = true, ...params } = options;
  return useQuery<AgentStatsResponse>({
    queryKey: agentsKeys.stats(params),
    queryFn: () => fetchAgentStats(params),
    enabled,
    staleTime: 0, // 통계 모달은 열 때마다 최신 수치를 본다
  });
}

// ────────────── 회사별 규칙 가져오기 ──────────────

/** 규칙 파일을 가져오면 바뀌는 회사·규칙 미리보기(DB 변경 없음). */
export function useAgentRulesImportPreview(file: AgentRulesFile) {
  return useQuery<AgentRulesImportPreview>({
    queryKey: [...AGENTS_QUERY_KEY, "rules-import-preview", file],
    queryFn: () => previewAgentRulesImport(file),
    staleTime: 0,
    gcTime: 0,
  });
}

/** 선택한 회사의 규칙만 반영. 성공하면 목록·미리보기를 다시 불러온다. */
export function useApplyAgentRulesImport() {
  const qc = useQueryClient();
  return useMutation<{ applied: string[] }, Error, { file: AgentRulesFile; agentIds: string[] }>({
    mutationFn: ({ file, agentIds }) => applyAgentRulesImport(file, agentIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });
}
